import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import waitlistService from '../api/waitlistService';

const useWaitlistModal = () => {
  const [showWaitlistModal, setShowWaitlistModal] = useState(false);
  const { currentUser } = useAuth();
  const location = useLocation();

  useEffect(() => {
    const checkWaitlistAndShowModal = async () => {
      // Only check if user is authenticated and has an email
      if (!currentUser?.email) {
        console.log('🎉 useWaitlistModal: No user email, skipping waitlist check');
        return;
      }

      console.log('🎉 useWaitlistModal: Starting waitlist check for:', currentUser.email);

      try {
        // Check if we've already shown the modal for this user
        const modalShownKey = `waitlist_modal_shown_${currentUser.email}`;
        const modalAlreadyShown = localStorage.getItem(modalShownKey) === 'true';
        
        if (modalAlreadyShown) {
          console.log('🎉 Waitlist modal already shown for this user, skipping');
          return;
        }

        console.log('🎉 Checking if user is on waitlist...');
        // Check if the user's email is on the waitlist
        const waitlistResult = await waitlistService.checkEmailOnWaitlist(currentUser.email);
        console.log('🎉 Waitlist check result:', waitlistResult);
        
        if (waitlistResult.onWaitlist) {
          console.log('🎉 User is on waitlist, showing success modal');
          
          // Mark modal as shown immediately to prevent duplicates
          localStorage.setItem(modalShownKey, 'true');
          
          // Add a short delay to allow dashboard to load first
          setTimeout(() => {
            console.log('🎉 Setting showWaitlistModal to true');
            setShowWaitlistModal(true);
          }, 1500);
        } else {
          console.log('👤 User is not on waitlist, no modal needed');
        }
      } catch (error) {
        console.error('Error checking waitlist status:', error);
        // Don't show modal if there's an error
      }
    };

    // Check if we're on a dashboard page or member home
    const isDashboardPage = location.pathname.includes('/dashboard') || 
                           location.pathname.includes('/admin/dashboard') ||
                           location.pathname.includes('/member-home') ||
                           location.pathname === '/admin' ||
                           location.pathname === '/member';
    
    console.log('🎉 useWaitlistModal: Current user:', currentUser?.email);
    console.log('🎉 useWaitlistModal: Is dashboard page:', isDashboardPage);
    console.log('🎉 useWaitlistModal: Current path:', location.pathname);
    
    if (isDashboardPage && currentUser?.email) {
      console.log('🎉 useWaitlistModal: Running waitlist check...');
      checkWaitlistAndShowModal();
    }
  }, [currentUser, location.pathname]);

  const closeWaitlistModal = async () => {
    setShowWaitlistModal(false);
    
    // Mark waitlist entry as completed when user closes the modal
    if (currentUser?.email) {
      try {
        await waitlistService.completeWaitlistEntry(currentUser.email);
        console.log('✅ Waitlist entry marked as completed after modal close');
      } catch (error) {
        console.log('ℹ️ Waitlist completion note:', error.message);
        // Don't fail if waitlist update fails
      }
      
      // Also set localStorage as backup
      const modalShownKey = `waitlist_modal_shown_${currentUser.email}`;
      localStorage.setItem(modalShownKey, 'true');
    }
  };

  return {
    showWaitlistModal,
    closeWaitlistModal
  };
};

export default useWaitlistModal;
